"use client";

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { addDaysISO } from "@/lib/study-plan";
import type { ScheduledTask } from "@/lib/study-plan-state";
import {
  PLAN_DOT_SOLID,
  WEEKDAY_KO,
  codeLangLabel,
  dateFromISO,
  isExamKind,
  isSameMonth,
  startOfMonthGridISO,
  taskEmoji,
} from "./schedule-shared";

/**
 * 셀에 표시할 이벤트 막대 최대 개수(초과 시 "+k개"). 6주 그리드라 셀이 너무 길어지지 않게 제한.
 * sm 미만(모바일)에서는 점(dot) 폴백이라 별도로 MAX_DOTS_MOBILE 만큼만 점을 찍는다.
 */
const MAX_BARS = 3;
const MAX_DOTS_MOBILE = 4;

/**
 * 막대 채움색(구글 캘린더식 단일 라인 이벤트). 계획 칩(PLAN_CHIP)과 같은 "연한 틴트 + 동계열 진한
 * 텍스트" 패밀리: 이론=blue / 코딩=green. ui-patterns dark-mode-colors §3 의 light↔dark 페어 규칙.
 */
const PLAN_BAR: Record<string, string> = {
  "theory-4week":
    "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200",
  "coding-6week":
    "bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-200",
};

/** 시험(감자시험 계열) 강조 막대 — amber 계열. */
const EXAM_BAR =
  "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200";

/**
 * 월(Month) 뷰 — 읽기 전용 개요 (JCG-2310).
 *
 * 6주 표준 월 그리드(이전/다음달 흐리게). 각 셀은 **구글 캘린더식 단일 라인 이벤트 막대 스택**:
 * 상단 날짜 숫자 + 그 아래로 그날 task 를 가로 꽉 찬 색 막대(딱 1줄, truncate)로 세로로 쌓는다.
 * 막대 채움색=plan 색(이론=blue/코딩=green), 시험(isExamKind)=amber. done=흐림+취소선, skipped=흐림.
 * 막대가 MAX_BARS 초과면 "+k개" 오버플로. sm 미만(모바일)은 칼럼이 좁아 막대 텍스트가 안 보이므로
 * 점(dot) 폴백. 셀 클릭 → 그 날 상세 모달(onOpenDay, 체크·DnD 순서변경). 계획 범위 밖(빈 날) 비활성.
 */
export default function MonthGrid({
  monthAnchorISO,
  todayISO,
  tasksByDate,
  rangeMinISO,
  rangeMaxISO,
  onOpenDay,
}: {
  /** 표시할 달의 임의 날짜(보통 1일). */
  monthAnchorISO: string;
  todayISO: string;
  tasksByDate: Map<string, ScheduledTask[]>;
  rangeMinISO: string;
  rangeMaxISO: string;
  /** 셀(날짜) 클릭 → 그 주 week 뷰로. */
  onOpenDay: (dateISO: string) => void;
}) {
  const gridStartISO = useMemo(
    () => startOfMonthGridISO(monthAnchorISO),
    [monthAnchorISO],
  );
  const cells = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDaysISO(gridStartISO, i)),
    [gridStartISO],
  );

  return (
    <div>
      {/* 요일 헤더 */}
      <div className="mb-1 grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEKDAY_KO.map((wd, i) => (
          <div
            key={wd}
            className={cn(
              "py-1 text-center text-[11px] font-semibold",
              i === 0 && "text-red-500/80 dark:text-red-400/80",
              i === 6 && "text-blue-500/80 dark:text-blue-400/80",
              i > 0 && i < 6 && "text-muted-foreground",
            )}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* 6주 그리드 */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((iso) => (
          <MonthCell
            key={iso}
            dateISO={iso}
            monthAnchorISO={monthAnchorISO}
            todayISO={todayISO}
            tasks={tasksByDate.get(iso) ?? []}
            inRange={iso >= rangeMinISO && iso <= rangeMaxISO}
            onOpenDay={onOpenDay}
          />
        ))}
      </div>
    </div>
  );
}

function MonthCell({
  dateISO,
  monthAnchorISO,
  todayISO,
  tasks,
  inRange,
  onOpenDay,
}: {
  dateISO: string;
  monthAnchorISO: string;
  todayISO: string;
  tasks: ScheduledTask[];
  inRange: boolean;
  onOpenDay: (dateISO: string) => void;
}) {
  const d = dateFromISO(dateISO);
  const dayNum = d.getUTCDate();
  const inMonth = isSameMonth(dateISO, monthAnchorISO);
  const isToday = dateISO === todayISO;
  const hasExam = tasks.some((t) => isExamKind(t.kind));
  // 건너뛴 task 는 셀 표시에서 제외(주 뷰와 동일). 부모(ScheduleCalendar tasksByDate)가 이미 정렬해 내려준다.
  const trackable = tasks.filter((t) => !t.skipped);
  const visible = trackable.slice(0, MAX_BARS);
  const barOverflow = Math.max(0, trackable.length - MAX_BARS);
  const visibleDots = trackable.slice(0, MAX_DOTS_MOBILE);
  const dotOverflow = Math.max(0, trackable.length - MAX_DOTS_MOBILE);

  // 계획 기간 안의 날이면 클릭 가능(빈 날도 그 주로 점프). 인접 달 칸이라도 범위 안이면 클릭 가능 —
  // 막대(task)는 보이는데 클릭이 안 되던 불일치 수정(JCG-2310 리뷰). 범위 밖은 비활성.
  const clickable = inRange;

  // 셀 안쪽 콘텐츠: 날짜 숫자 + (sm+) 이벤트 막대 스택 / (sm 미만) 점 폴백.
  const content = (
    <>
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums",
          isToday
            ? "bg-amber-500 font-bold text-white dark:bg-amber-400 dark:text-amber-950"
            : inMonth
              ? "font-medium text-foreground"
              : "text-muted-foreground/40",
        )}
      >
        {dayNum}
      </span>

      {trackable.length > 0 && (
        <>
          {/* sm+ : 구글 캘린더식 단일 라인 이벤트 막대 스택 */}
          <span className="mt-1 hidden w-full min-w-0 flex-col gap-0.5 sm:flex">
            {visible.map((task) => (
              <PlanBar key={task.uid} task={task} />
            ))}
            {barOverflow > 0 && (
              <span className="px-1 text-[10px] font-semibold leading-tight text-muted-foreground">
                +{barOverflow}개
              </span>
            )}
          </span>

          {/* sm 미만 : 칼럼이 좁아 막대 텍스트가 안 보이므로 점(dot) 폴백 */}
          <span className="mt-1 flex flex-wrap items-center justify-center gap-0.5 sm:hidden">
            {visibleDots.map((task) => (
              <PlanDot key={task.uid} task={task} />
            ))}
            {dotOverflow > 0 && (
              <span className="text-[9px] font-semibold leading-none text-muted-foreground">
                +{dotOverflow}
              </span>
            )}
          </span>
        </>
      )}
    </>
  );

  // 셀 기본 형태: 정사각형이 아니라 막대 스택을 담는 세로로 긴 직사각(min-height). 균일 셀.
  const cellBase =
    "flex min-h-[3.25rem] flex-col items-center justify-start rounded-lg border p-1 sm:min-h-[6rem] sm:items-stretch";

  // 범위 밖 / 다른 달 빈 셀은 비활성(클릭 불가).
  if (!clickable) {
    return (
      <div
        className={cn(cellBase, "border-transparent", !inMonth && "opacity-50")}
        aria-hidden={!inMonth}
      >
        {content}
      </div>
    );
  }

  // 막대 텍스트는 truncate 되더라도 스크린리더에는 그날 학습 제목 전체를 읽어준다.
  // 다국어 코딩 task 는 언어까지 붙여 같은 제목 3개를 구분(JCG-2658). 빈 값이면 제목만.
  const titleList =
    trackable.length > 0
      ? `: ${trackable
          .map((t) => {
            const lang = codeLangLabel(t.codeLang);
            return lang ? `${t.title} ${lang}` : t.title;
          })
          .join(", ")}`
      : "";

  return (
    <button
      type="button"
      onClick={() => onOpenDay(dateISO)}
      aria-label={`${d.getUTCMonth() + 1}월 ${dayNum}일 일정 상세 보기${
        trackable.length > 0 ? `, 학습 ${trackable.length}개${titleList}` : ""
      }`}
      className={cn(
        cellBase,
        "text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
        isToday
          ? "border-amber-400 dark:border-amber-500"
          : "border-border hover:border-amber-300 hover:bg-accent dark:hover:border-amber-700",
        hasExam && "bg-amber-50/50 dark:bg-amber-950/20",
        // 인접 달 칸이지만 범위 안이라 클릭 가능한 셀은 흐리게(구글 캘린더식 spillover).
        !inMonth && "opacity-50",
      )}
    >
      {content}
    </button>
  );
}

/** sm+ 단일 라인 이벤트 막대(딱 1줄, truncate). 채움색=plan 색/시험=amber. done=흐림+취소선, skipped=흐림. */
function PlanBar({ task }: { task: ScheduledTask }) {
  const dimmed = task.done || task.skipped;
  const tone = isExamKind(task.kind)
    ? EXAM_BAR
    : (PLAN_BAR[task.planId] ?? PLAN_BAR["theory-4week"]);
  // 셀이 극도로 좁아 CODE_LANG_BADGE(테두리 뱃지) 대신 굵은 텍스트 프리픽스로 언어를 표시(JCG-2658).
  // 막대 텍스트색(대비 확보됨)을 그대로 상속하므로 계획색·amber 위에 새 색을 얹지 않는다.
  // shrink-0 라 제목이 truncate 돼도 언어는 남아 같은 제목 3개를 구분한다. 스크린리더는 셀 aria-label 로 전달.
  const langLabel = codeLangLabel(task.codeLang);
  return (
    <span
      className={cn(
        "flex w-full min-w-0 items-center gap-1 rounded-sm px-1 py-0.5 text-[10px] font-medium leading-tight",
        tone,
        dimmed && "opacity-50",
      )}
      aria-hidden
    >
      <span className="shrink-0 text-[9px] leading-none">{taskEmoji(task)}</span>
      {langLabel && (
        <span className="shrink-0 font-bold">{langLabel}</span>
      )}
      <span className={cn("min-w-0 truncate", task.done && "line-through")}>
        {task.title}
      </span>
    </span>
  );
}

/** sm 미만 점 폴백. 채움(done)=solid plan/amber, 미완=opacity 낮춘 같은 색. */
function PlanDot({ task }: { task: ScheduledTask }) {
  const solid = isExamKind(task.kind)
    ? "bg-amber-500 dark:bg-amber-400"
    : PLAN_DOT_SOLID[task.planId];
  return (
    <span
      className={cn("h-1.5 w-1.5 rounded-full", solid, !task.done && "opacity-40")}
      aria-hidden
    />
  );
}
